import {noop} from '@benchling/js-utils';
import {Divider, Group, Stack, Text, TextInput} from '@mantine/core';
import React from 'react';

import * as analytics from 'analytics/Analytics';
import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import * as benchlingUtil from 'BenchlingUtil';
import {ANALYTICS_MODULE} from 'constants/AnalyticsConstants';
import * as FileBrowserConstants from 'constants/FileBrowserConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import FileBrowserActions from 'file-browser/FileBrowserActions';
import FolderDAOCache from 'file-browser/FolderDAOCache';
import FolderSelector, {DEFAULT_PROJECT_FOLDER_LABEL_TEXT} from 'file-browser/FolderSelector';
import filestates from 'FileStates';
import FolderDefaults, {FolderDefaultsKey} from 'FolderDefaults';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import ProteinActions from 'molbio/aa-sequences/AaSequenceActions';
import ProteinDAO, {ImportProteinResponse} from 'molbio/aa-sequences/AaSequenceDAO';
import CreateProteinForm, {getCreateFormValidation} from 'molbio/aa-sequences/CreateAaSequenceForm';
import {PROTEIN_DEFAULT_DATABASE_IMPORT_PREFERENCE_KEY} from 'molbio/aa-sequences/util/AaSequenceConstants';
import {AA_SEQUENCE_IMPORT_MODAL_TAB} from 'molbio/MolBioConstants';
import notifications, {FOREVER_DURATION} from 'notifications';
import preferences from 'Preferences';
import {AllTagSchemasQuery} from 'registry/graphql/__generated/AllTagSchemasQuery';
import {ALL_TAG_SCHEMAS_QUERY} from 'registry/graphql/AllTagSchemasQuery';
import {BioentityLinkFieldWithTagSchema} from 'schemas/fields/SchemaFieldConstants';
import TagSchemaSelector from 'schemas/shared/TagSchemaSelector';
import {SortKey} from 'search/SearchConstants';
import {StudySearchItem} from 'studies/StudySearchInput';
import {filterTagSchemasGraphQL} from 'tag-schema/filterTagSchemas';
import * as TrayTaskActions from 'tray/TrayTaskActions';
import * as TrayTaskConstants from 'tray/TrayTaskConstants';
import FormGroup from 'ui-library/bs/FormGroup';
import {ButtonStyle} from 'ui-library/buttons/Button';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import {GENERIC_ERROR_MESSAGE} from 'ui-library/ErrorConstants';
import FileChooser from 'ui-library/FileChooser';
import CheckboxInput from 'ui-library/inputs/CheckboxInput';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import Menu from 'ui-library/menus/Menu';
import {MenuWidth} from 'ui-library/menus/MenuConstants';
import Option from 'ui-library/menus/Option';
import * as ModalContainer from 'ui-library/ModalContainer';
import Modal from 'ui-library/modals/Modal';
import {Tab, TabContainer} from 'ui-library/TabContainer';
import * as ImportUtil from 'util/ImportUtil';
import {InputChangeEvent, Optional} from 'util/Types';
import AddToWorklistForm, {
  AddToWorklistFormProps,
  AddToWorklistSubmitProps,
  useAddToWorklistForm,
  WorklistFormSelectorType,
  AddToWorklistProps,
} from 'worklists/AddToWorklistForm';
import {WorklistFormType, WorklistType} from 'worklists/WorklistConstants';

const IMPORT_PROTEIN_MODAL_KEY = 'IMPORT_PROTEIN_MODAL_KEY';

function closeImportProteinModal(): void {
  ModalContainer.close(IMPORT_PROTEIN_MODAL_KEY);
}

export function openImportProteinModal(props: Omit<ImportProteinModalProps, 'onRequestClose'>): void {
  ModalContainer.open(
    IMPORT_PROTEIN_MODAL_KEY,
    <ImportProteinModal {...props} onRequestClose={closeImportProteinModal} />
  );
}

const trackImportFail = (importFlow: AA_SEQUENCE_IMPORT_MODAL_TAB, error: string): void => {
  analytics.track('protein', 'import_fail', {
    product: ANALYTICS_MODULE.MOLBIO,
    importFlow,
    error,
  });
};

type ProteinImportFinishedResult =
  | ImportProteinResponse
  | {
      // This additional case is for the "upload AA sequence file failed" case. See the `_proteinImportFinished`
      // call in `_handleStartProteinUpload` below.
      results?: never;
      error: string;
      warningMessage?: never;
    };

// Called when the import task ends (though the import itself may have failed)
// taskId - the TaskTray id corresponding to this AA sequence file import
// results - list of AA sequence dicts
// error - nullable string describing the error if parsing the file failed
// warningMessage - either null or a string describing a warning to give. This may happen in either
//   the success or error cases.
const _proteinImportFinished = (
  taskId: string,
  saveInWorklist: boolean,
  result: ProteinImportFinishedResult
): ProteinImportFinishedResult => {
  const {results: maybeResults, error, warningMessage} = result;
  if (warningMessage) {
    notifications.warn(warningMessage, {duration: FOREVER_DURATION});
  }

  if (error == null) {
    analytics.track('protein', 'import_success', {
      product: ANALYTICS_MODULE.MOLBIO,
      importFlow: AA_SEQUENCE_IMPORT_MODAL_TAB.FILE,
    });
    const results = maybeResults!;
    const numProteins = results.length;
    const {folder} = results[0];
    const onTaskClick = (): void => {
      if (results.length === 1) {
        FileBrowserActions.openFile(results[0].id);
      } else {
        filestates.get().openFolder(folder.api_identifier, folder.url, {
          reverse: '1',
          section: FileBrowserConstants.INVENTORY_SECTION,
          sort: SortKey.SORT_MODIFIED_AT_KEY,
        });
      }
      TrayTaskActions.removeTrayTask(taskId);
    };

    let trayTaskOptions;
    if (saveInWorklist) {
      // The tray task for each AA sequence file is less useful since we'll be giving the user a link to
      // the full worklist, so we remove the actions and let it disappear more quickly.
      trayTaskOptions = {actions: [], duration: 5};
    } else {
      trayTaskOptions = {onHeadingClick: onTaskClick, onSubheadingClick: onTaskClick};
    }

    TrayTaskActions.updateTrayTask(taskId, {
      progress: TrayTaskConstants.SUCCESS_PROGRESS,
      subheading: `Imported ${numProteins} ${benchlingUtil.pluralize(numProteins, 'AA sequence')} to ${
        folder.name
      }`,
      ...trayTaskOptions,
    });
  } else {
    trackImportFail(AA_SEQUENCE_IMPORT_MODAL_TAB.FILE, error);
    TrayTaskActions.updateTrayTask(taskId, {
      progress: TrayTaskConstants.FAILURE_PROGRESS,
      subheading: 'AA Sequence Import failed',
      error,
    });
  }
  return result;
};

const importProteinFromLocation = (
  folderId: string,
  tagSchemaId: string | undefined,
  location: string,
  taskId: string,
  saveInWorklist: boolean
): JQueryPromise<ProteinImportFinishedResult> =>
  ProteinDAO.importProtein({location, folderId, tagSchemaId})
    .then((result: ImportProteinResponse) => _proteinImportFinished(taskId, saveInWorklist, result))
    .done(() => {
      FolderDAOCache.invalidateFolderContents(folderId);
    })
    .fail((res) => {
      const message = res.responseText ?? 'Uploading AA sequence file failed - please try again';
      trackImportFail(AA_SEQUENCE_IMPORT_MODAL_TAB.FILE, message);
      // For some reason, we weren't able to start this task
      TrayTaskActions.updateTrayTask(taskId, {
        progress: TrayTaskConstants.FAILURE_PROGRESS,
        error: message,
      });
    });

export interface CreateConfirmPayload {
  name: string;
  folderId: string;
  translationLink?: BioentityLinkFieldWithTagSchema | null;
  tagSchemaId?: string;
  studyIds?: Array<string>;
}

interface ImportProteinModalProps {
  initialFolderId?: Optional<string>;
  onRequestClose: () => void;
  initialTab?: AA_SEQUENCE_IMPORT_MODAL_TAB;
}

interface ImportProteinModalPropsInner extends ImportProteinModalProps {
  worklistForm: AddToWorklistFormProps & AddToWorklistProps & AddToWorklistSubmitProps;
}

interface ImportProteinModalState {
  folderId: Optional<string>;
  proteinFiles: Array<File>;
  tagSchemaId?: string;
  accessions: string;
  database: string;
  activeTab: AA_SEQUENCE_IMPORT_MODAL_TAB;
  saveInWorklist: boolean;
  // Create tab state
  createName: string;
  createTagSchemaId: string | null;
  createSelectedStudyItems: Array<StudySearchItem>;
}

export class ImportProteinModalInner extends React.Component<
  ImportProteinModalPropsInner,
  ImportProteinModalState
> {
  override state: ImportProteinModalState = {
    folderId: this.props.initialFolderId ?? FolderDefaults.get(FolderDefaultsKey.INVENTORY),
    proteinFiles: [],
    tagSchemaId: undefined,
    // Comma-separated list of accessions, ignoring whitespace
    accessions: '',
    database: preferences.get(PROTEIN_DEFAULT_DATABASE_IMPORT_PREFERENCE_KEY) || 'NCBI',
    activeTab: this.props.initialTab ?? AA_SEQUENCE_IMPORT_MODAL_TAB.CREATE,
    saveInWorklist: false,
    // Create tab state
    createName: '',
    createTagSchemaId: null,
    createSelectedStudyItems: [],
  };

  handleClickTab = (tab: AA_SEQUENCE_IMPORT_MODAL_TAB): void => {
    this.setState({activeTab: tab});
    analytics.track('protein', 'import_tab_click', {
      product: ANALYTICS_MODULE.MOLBIO,
      importFlow: tab,
    });
  };

  _handleChangeTagSchemaId = (tagSchema: {id: string} | null): void => {
    assert(tagSchema != null, 'TagSchemaSelector returned null for tagSchema');
    this.setState({tagSchemaId: tagSchema.id});
  };

  _handleStartProteinUpload = (): void => {
    analytics.track('protein', 'import_click', {
      product: ANALYTICS_MODULE.MOLBIO,
      importFlow: AA_SEQUENCE_IMPORT_MODAL_TAB.FILE,
    });
    const requests = Promise.allSettled<ProteinImportFinishedResult>(
      this.state.proteinFiles.map((proteinFile) => {
        const taskId = TrayTaskActions.createTrayTask({
          heading: proteinFile.name,
        });
        return ImportUtil.upload(proteinFile)
          .progress((progress: number) => {
            TrayTaskActions.updateTrayTask(taskId, {progress});
          })
          .fail((res: Response) => {
            const error = notifications.getFriendlyError(res) ?? GENERIC_ERROR_MESSAGE;
            trackImportFail(AA_SEQUENCE_IMPORT_MODAL_TAB.FILE, error);
            _proteinImportFinished(taskId, this.state.saveInWorklist, {error});
          })
          .then((s3location: string) =>
            importProteinFromLocation(
              this.state.folderId!,
              this.state.tagSchemaId,
              s3location,
              taskId,
              this.state.saveInWorklist
            )
          );
      })
    );

    if (this.state.saveInWorklist) {
      // Once all the AA sequence files have been uploaded, add the AA sequences to a worklist.
      requests
        .then((responses) => {
          const ids = responses
            .filter((response) => response.status === 'fulfilled')
            .map(({value}: {value: ProteinImportFinishedResult}) =>
              value.results ? value.results.map((result) => result.id) : []
            )
            .flat();
          if (ids.length > 0) {
            this.props.worklistForm.addItems(ids);
          }
        })
        .catch((error) => {
          notifications.error(error);
        });
    }

    this.props.onRequestClose();
  };

  handleFilesSelected = (...files: Array<File>): void => {
    analytics.track('protein', 'select_files', {
      product: ANALYTICS_MODULE.MOLBIO,
      importFlow: AA_SEQUENCE_IMPORT_MODAL_TAB.FILE,
    });
    this.setState({proteinFiles: [...this.state.proteinFiles, ...files]});
  };

  _handleSetFolderId = (e: {target: {value: string}}): void => {
    const folderId = e.target.value;
    this.setState({folderId});
    FolderDefaults.set(FolderDefaultsKey.INVENTORY, folderId);
  };

  _handleSetAccessions = (e: InputChangeEvent): void => {
    if (!this.state.accessions) {
      analytics.track('protein', 'select_files', {
        product: ANALYTICS_MODULE.MOLBIO,
        importFlow: AA_SEQUENCE_IMPORT_MODAL_TAB.DB,
      });
    }
    const accessions = e.target.value;
    this.setState({accessions});
  };

  _handleSetDatabase = (database: string): void => {
    this.setState({database});
  };

  _getAccessionList = (): Array<string> =>
    this.state.accessions
      .split(',')
      .map((accession) => accession.trim())
      .filter((accession) => accession !== '');

  _handleDBImportProtein = (): void => {
    analytics.track('protein', 'import_click', {
      product: ANALYTICS_MODULE.MOLBIO,
      importFlow: AA_SEQUENCE_IMPORT_MODAL_TAB.DB,
    });
    const accessionList = this._getAccessionList();
    if (accessionList.length > 20) {
      const error = 'Please import no more than 20 AA sequences at a time.';
      trackImportFail(AA_SEQUENCE_IMPORT_MODAL_TAB.DB, error);
      notifications.warn(error);
    } else {
      for (const accession of this._getAccessionList()) {
        ProteinActions.dbImportProtein({
          accession,
          database: this.state.database,
          folderId: this.state.folderId!,
        });
      }
      this.props.onRequestClose();
    }
  };

  renderFolderSelector = (): JSX.Element => (
    <FormGroup>
      <FieldLabel label={DEFAULT_PROJECT_FOLDER_LABEL_TEXT} isRequired={true} />
      <FolderSelector
        enableRedesign={true}
        showInventory={true}
        value={this.state.folderId}
        onChange={this._handleSetFolderId}
        unsetOnFetchFail={true}
      />
    </FormGroup>
  );

  _getModalButtons = (): Array<{
    text: string;
    cancel?: boolean;
    submit?: boolean;
    disabled?: boolean;
    style?: ButtonStyle;
    onClick?: () => void;
  }> => {
    if (this.state.activeTab === AA_SEQUENCE_IMPORT_MODAL_TAB.CREATE) {
      const {isValid} = getCreateFormValidation({
        name: this.state.createName,
        folderId: this.state.folderId,
        tagSchemaId: this.state.createTagSchemaId,
      });
      return [
        {
          text: 'Cancel',
          cancel: true,
        },
        {
          text: 'Create',
          submit: true,
          style: ButtonStyle.PRIMARY,
          disabled: !isValid,
          onClick: this._handleCreateConfirm,
        },
      ];
    } else if (this.state.activeTab === AA_SEQUENCE_IMPORT_MODAL_TAB.FILE) {
      return [
        {
          text: 'Cancel',
          cancel: true,
        },
        {
          text: 'Import',
          submit: true,
          style: ButtonStyle.PRIMARY,
          disabled:
            this.state.proteinFiles.length === 0 ||
            this.state.folderId == null ||
            (this.state.saveInWorklist && this.props.worklistForm.isDisabled),
          onClick: this._handleStartProteinUpload,
        },
      ];
    } else {
      // DB import tab
      return [
        {
          text: 'Cancel',
          cancel: true,
        },
        {
          text: 'Import',
          submit: true,
          style: ButtonStyle.PRIMARY,
          disabled: this._getAccessionList().length === 0 || this.state.folderId == null,
          onClick: this._handleDBImportProtein,
        },
      ];
    }
  };

  renderUploadFilesTab = ({shouldShowTagSchemas}: {shouldShowTagSchemas: boolean}): JSX.Element => (
    <Stack spacing="0">
      <Text size="sm" color="dimmed" mb="md">
        Upload FASTA, GenPept, Uniprot XML, and Vector NTI .pa4 files. Drag in multiple at once.
      </Text>
      {this.renderFolderSelector()}
      {shouldShowTagSchemas && (
        <FormGroup>
          <FieldLabel label="Schema" />
          <TagSchemaSelector
            selectedTagSchemaId={this.state.tagSchemaId}
            onChange={this._handleChangeTagSchemaId}
            oligoAwareFolderItemTypes={[ModelTypeConstants.PROTEIN]}
          />
        </FormGroup>
      )}
      <FormGroup>
        {this.state.proteinFiles.length > 0 && (
          <Stack spacing="xs" mb="xs">
            {this.state.proteinFiles.map((proteinFile) => (
              <Text key={proteinFile.name} size="sm" color="dimmed">
                {proteinFile.name}
              </Text>
            ))}
          </Stack>
        )}
        <FileChooser onSelectFiles={this.handleFilesSelected} multiple={true} />
      </FormGroup>
      {BenchlingConfig.WORKLISTS_ENABLE_WORKLISTS && (
        <>
          <Divider />
          <Stack spacing="sm" pt="md" pb="md">
            <CheckboxInput
              id="import-protein-modal-save-in-worklist-checkbox"
              checked={this.state.saveInWorklist}
              label="Add sequences to worklist"
              onChange={(e) => {
                this.setState({saveInWorklist: e.target.checked});
              }}
            />
            {this.state.saveInWorklist && (
              <AddToWorklistForm
                {...this.props.worklistForm}
                chipsAreLinks={false}
                selectorType={WorklistFormSelectorType.TOGGLE_GROUP}
              />
            )}
          </Stack>
        </>
      )}
    </Stack>
  );

  renderImportFromDatabaseTab = (): JSX.Element => (
    <Stack spacing="0">
      <Text size="sm" color="dimmed" mb="md">
        Import multiple sequences from a database by entering comma-separated accession numbers.
      </Text>
      <Group grow={true} align="flex-start">
        <FormGroup>
          <FieldLabel label="Accessions" isRequired={true} />
          <TextInput
            placeholder="e.g., P68871, NP_000509, Q9TSP1"
            value={this.state.accessions}
            onChange={(e) => {
              this._handleSetAccessions(e as InputChangeEvent);
            }}
          />
        </FormGroup>
        <FormGroup>
          <FieldLabel label="Database" />
          <SelectDropdown
            displayName={this.state.database}
            menu={
              <Menu width={MenuWidth.FIT_CONTENT}>
                {['NCBI', 'UniProt'].map((db) => (
                  <Option
                    key={db}
                    onSelect={() => {
                      this._handleSetDatabase(db);
                    }}
                  >
                    {db}
                  </Option>
                ))}
              </Menu>
            }
          />
        </FormGroup>
      </Group>
      {this.renderFolderSelector()}
    </Stack>
  );

  _handleCreateConfirm = (): void => {
    const confirmPayload: CreateConfirmPayload = {
      folderId: this.state.folderId!,
      name: this.state.createName,
    };
    if (this.state.createTagSchemaId != null) {
      confirmPayload.tagSchemaId = this.state.createTagSchemaId;
    }
    if (this.state.createSelectedStudyItems.length > 0) {
      confirmPayload.studyIds = this.state.createSelectedStudyItems.map((studyItem) => studyItem.id);
    }
    ProteinActions.createAndOpen({
      name: confirmPayload.name,
      aminoAcids: '',
      folderId: confirmPayload.folderId,
      tagSchemaId: confirmPayload.tagSchemaId,
      studyIds: confirmPayload.studyIds,
    });
    this.props.onRequestClose();
  };

  renderCreateNewTab = (): JSX.Element => (
    <CreateProteinForm
      name={this.state.createName}
      folderId={this.state.folderId}
      tagSchemaId={this.state.createTagSchemaId}
      selectedStudyItems={this.state.createSelectedStudyItems}
      onNameChange={(name) => {
        this.setState({createName: name});
      }}
      onFolderIdChange={(folderId) => {
        this.setState({folderId});
        FolderDefaults.set(FolderDefaultsKey.INVENTORY, folderId);
      }}
      onTagSchemaIdChange={(tagSchemaId) => {
        this.setState({createTagSchemaId: tagSchemaId});
      }}
      onSelectedStudyItemsChange={(studyItems) => {
        this.setState({createSelectedStudyItems: studyItems});
      }}
      oligoAwareFolderItemTypes={[ModelTypeConstants.PROTEIN]}
      // None of these props are actually used, since translation link fields is undefined.
      // TODO (BNCH-138848): Nest these props in a single object.
      linkFieldLabel="Translation link"
      translationLink={undefined}
      translationLinkFields={undefined}
      onTranslationLinkChange={noop}
    />
  );

  override render(): JSX.Element {
    return (
      // We need to fetch all `TagSchema`s to determine if we should show the `TagSchemaSelector`, based on if
      // there are any AA sequence schemas to choose from. Ideally this would be a simpler query, but for now, we
      // use the shared `ALL_TAG_SCHEMAS_QUERY` to benefit from shared caching.
      <BenchlingQuery<AllTagSchemasQuery>
        query={ALL_TAG_SCHEMAS_QUERY}
        renderLoading={BenchlingQuery.DEFAULT_RENDER_LOADING}
      >
        {(data) => {
          const shouldShowTagSchemas =
            filterTagSchemasGraphQL(data.tagSchemas, {
              oligoAwareFolderItemTypes: [ModelTypeConstants.PROTEIN],
            }).length > 0;
          return (
            <Modal
              title="Create AA sequence"
              showHeaderBorder={false}
              enableRedesign={true}
              onRequestClose={this.props.onRequestClose}
              buttons={this._getModalButtons()}
            >
              <TabContainer activeTab={this.state.activeTab} onRequestTabChange={this.handleClickTab}>
                <Tab tabKey={AA_SEQUENCE_IMPORT_MODAL_TAB.CREATE} title="Create new">
                  {this.renderCreateNewTab()}
                </Tab>
                <Tab tabKey={AA_SEQUENCE_IMPORT_MODAL_TAB.FILE} title="Upload files">
                  {this.renderUploadFilesTab({shouldShowTagSchemas})}
                </Tab>
                <Tab tabKey={AA_SEQUENCE_IMPORT_MODAL_TAB.DB} title="Import from database">
                  {this.renderImportFromDatabaseTab()}
                </Tab>
              </TabContainer>
            </Modal>
          );
        }}
      </BenchlingQuery>
    );
  }
}

export default function ImportProteinModal(props: ImportProteinModalProps): JSX.Element {
  const worklistFormSettings = {
    autoFocus: false,
    ids: [],
    worklistType: WorklistType.BIOENTITY,
    useFullWidthToggle: true,
    refreshOptions: {
      refreshWorklistItemsSearch: true,
      refreshWorklistsSearch: true,
      refreshRecentWorklists: true,
    },
  };
  const worklistForm = useAddToWorklistForm(worklistFormSettings, WorklistFormType.NEW);
  return <ImportProteinModalInner {...props} worklistForm={{...worklistForm, ...worklistFormSettings}} />;
}
